import type { UseMutationResult } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useEffect } from 'react';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import type {
  LoanSignVerifyRequestBody,
  LoanSignVerifySuccessResponseBody,
  LoanSignRequestBodySchema,
  LoanSignSuccessResponseBody,
} from '@repo/api/endpoints/loan';
import type { ResponseError } from '@repo/api/types';
import { providerType, SmsId } from '@repo/shared/types';

import { LoanStoreData } from '@/components/loan-process/store/loan-process.store';
import { useLiberisIframeEvent } from '@/components/loan-process/loan-stages/stage-3/liberis-iframe/use-liberis-iframe-event';
import { ApplicationProcessing } from '@/components/loan-process/loan-stages/stage-0/application-processing/application-processing';
import { LiberisIframeComponent } from '@/components/loan-process/loan-stages/stage-3/liberis-iframe/liberis-iframe-component';

const getLoanSignPayload = (merchantResponseData: MerchantResponse, loanData: LoanStoreData) => {
  return {
    requestId: merchantResponseData.requestId,
    loanId: loanData.id,
    provider: providerType.enum.liberis,
    phoneNumber: null,
  };
};

export interface LoanSignMutation extends Pick<
  UseMutationResult<
    LoanSignSuccessResponseBody<'liberis'>,
    ResponseError,
    LoanSignRequestBodySchema
  >,
  'data' | 'status'
> {
  handleSignMutation: (data: LoanSignRequestBodySchema) => void;
  isPending: boolean;
}
export interface LoanVerifyMutation extends Pick<
  UseMutationResult<LoanSignVerifySuccessResponseBody, ResponseError, LoanSignVerifyRequestBody>,
  'status'
> {
  hasWrongSMSCode: boolean;
  handleSubmitForm: (data: LoanSignVerifyRequestBody) => void;
  isPending: boolean;
}

interface LiberisIframeProps {
  merchantResponseData: MerchantResponse;
  loanData: LoanStoreData;
  loanSMSIds: SmsId[];
  loanSignMutation: LoanSignMutation;
  loanVerifyMutation: LoanVerifyMutation;
  handleCloseButton: () => void;
}

export function LiberisIframe({
  handleCloseButton,
  loanData,
  loanVerifyMutation,
  merchantResponseData,
  loanSignMutation,
}: LiberisIframeProps) {
  const savedLoans = merchantResponseData?.data?.loans;
  const lastSavedLoan = savedLoans?.[savedLoans.length - 1];

  const { handleSubmitForm, isPending } = loanVerifyMutation;
  const {
    handleSignMutation,
    status: signStatus,
    data: signData,
    isPending: isSignPending,
  } = loanSignMutation;

  const handleSignVerify = () => {
    const payload = omit(getLoanSignPayload(merchantResponseData, loanData), 'phoneNumber');
    handleSubmitForm({
      ...payload,
      method: 'iframe',
      smsCode: null,
      smsId: null,
    });
  };

  useLiberisIframeEvent(handleSignVerify, isPending);

  useEffect(() => {
    if (lastSavedLoan?.stage !== 2 && !isSignPending) {
      handleSignMutation(getLoanSignPayload(merchantResponseData, loanData));
    }
  }, [lastSavedLoan]);

  const getIframeUrl = () => {
    if (signData) {
      return signData.data.url;
    }
    if (!loanData) return undefined;

    const liberisDocument = loanData.documents.find((document) => document.type === 'liberis');

    if (!liberisDocument) return undefined;

    return liberisDocument.url;
  };

  const iframeUrl = getIframeUrl();

  if (signStatus === 'pending') {
    return <ApplicationProcessing />;
  }

  return <LiberisIframeComponent handleCloseButton={handleCloseButton} iframeUrl={iframeUrl} />;
}
